import {
    Grid,
    Typography,
} from "@material-ui/core";
import {SocItem} from "../../domain/SocItem";
import {SectionProps} from "./SectionProps";
import {Controller} from "react-hook-form";
import {fieldLabels} from "./fieldLabels";
import {Error} from "../common/Error";
import {TextField} from "@ofqual/portal-components/components/TextField";

export const PreventingAndManagingMalpractice: React.FC<SectionProps<SocItem>> = (
    sectionProps
) => {
    const {control, disabled, errors} = sectionProps;
    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="body1">
                    <p>
                        These questions are about your capacity and aspects of your internal controls to prevent,
                        investigate and manage all instances of malpractice and maladministration, specifically linked
                        with
                        Condition A8: Malpractice and maladministration.
                    </p>
                    <p>
                        Where we refer to a `Centre`, we mean an organisation undertaking the delivery of assessment
                        (and
                        potentially other activities) to Learners on behalf of an awarding organisation. Centres are
                        typically educational institutions, training providers, or employers.
                    </p>
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h6">
                    Capacity and capability to prevent and manage malpractice and maladministration
                </Typography>
            </Grid>

            <Grid item xs={12} sm={12}>
                <Typography>
                    {fieldLabels.malpracticeGovernanceInformation}
                </Typography>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="malpracticeGovernanceInformation"
                    as={
                        <TextField
                            label={fieldLabels.pleaseEnterYourResponse}
                            readOnly={disabled}
                            data-test="malpracticeGovernanceInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.malpracticeGovernanceInformation?.type === "maxLength" && (
                    <Error data-test="malpracticeGovernanceInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.malpracticeGovernanceInformation?.type === "required" && (
                    <Error data-test="malpracticeGovernanceInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12} sm={12}>
                <Typography>
                    {fieldLabels.malpracticeOperationalInformation}
                </Typography>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="malpracticeOperationalInformation"
                    as={
                        <TextField
                            label={fieldLabels.pleaseEnterYourResponse}
                            readOnly={disabled}
                            data-test="malpracticeOperationalInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.malpracticeOperationalInformation?.type === "maxLength" && (
                    <Error data-test="malpracticeOperationalInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.malpracticeOperationalInformation?.type === "required" && (
                    <Error data-test="malpracticeOperationalInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12} sm={12}>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="malpracticeResourceInformation"
                    as={
                        <TextField
                            label={fieldLabels.malpracticeResourceInformation}
                            readOnly={disabled}
                            data-test="malpracticeResourceInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.malpracticeResourceInformation?.type === "maxLength" && (
                    <Error data-test="malpracticeResourceInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.malpracticeResourceInformation?.type === "required" && (
                    <Error data-test="malpracticeResourceInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h6">
                    Dealing with malpractice and maladministration
                </Typography>
                <Typography variant="body1">
                    What decision-making processes do you have in place for informing:
                </Typography>
            </Grid>

            <Grid item xs={12} sm={12}>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="decisionMakingRegulatorInformation"
                    as={
                        <TextField
                            label={fieldLabels.decisionMakingRegulatorInformation}
                            readOnly={disabled}
                            data-test="decisionMakingRegulatorInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.decisionMakingRegulatorInformation?.type === "maxLength" && (
                    <Error data-test="decisionMakingRegulatorInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.decisionMakingRegulatorInformation?.type === "required" && (
                    <Error data-test="decisionMakingRegulatorInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12} sm={12}>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="decisionMakingAOAffectedInformation"
                    as={
                        <TextField
                            label={fieldLabels.decisionMakingAOAffectedInformation}
                            readOnly={disabled}
                            data-test="decisionMakingAOAffectedInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.decisionMakingAOAffectedInformation?.type === "maxLength" && (
                    <Error data-test="decisionMakingAOAffectedInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.decisionMakingAOAffectedInformation?.type === "required" && (
                    <Error data-test="decisionMakingAOAffectedInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12} sm={12}>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="decisionMakingAONotifyInformation"
                    as={
                        <TextField
                            label={fieldLabels.decisionMakingAONotifyInformation}
                            readOnly={disabled}
                            data-test="decisionMakingAONotifyInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.decisionMakingAONotifyInformation?.type === "maxLength" && (
                    <Error data-test="decisionMakingAONotifyInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.decisionMakingAONotifyInformation?.type === "required" && (
                    <Error data-test="decisionMakingAONotifyInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12}>
                <Typography variant="body1">
                    If you receive intelligence from another awarding organisation about malpractice by a centre or
                    centre staff, how do you record, monitor, manage and use that information to manage any associated
                    risks to the delivery of your qualifications? You might wish to include information in relation to
                    your:
                    <ol type={"a"}>
                        <li>Centre approvals</li>
                        <li>Centre agreements, and</li>
                        <li>Centre monitoring.</li>
                    </ol>
                </Typography>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="intelligenceInformation"
                    as={
                        <TextField
                            label={fieldLabels.pleaseEnterYourResponse}
                            readOnly={disabled}
                            data-test="intelligenceInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.intelligenceInformation?.type === "maxLength" && (
                    <Error data-test="intelligenceInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.intelligenceInformation?.type === "required" && (
                    <Error data-test="intelligenceInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h6">
                    Generative Artificial Intelligence (AI) and malpractice and maladministration
                </Typography>
                <Typography variant="body1">
                    <p>
                        Although generative AI is not new, recent advances and public access to the technology mean that
                        the public can now use it more easily. This poses opportunities and challenges for the
                        qualifications and assessment sector. We wish to gather information about how awarding
                        organisations are considering the impact of generative AI in relation to malpractice and
                        maladministration.<br/>
                        Generative AI definition:
                    </p>
                    <p>
                        Generative AI technology uses foundation models trained on large volumes of data. It can be used
                        to produce artificially generated content such as text, audio, code, images, and videos.
                    </p>
                    <p>
                        Examples of generative AI tools include ChatGPT, Google Bard, Claude and Midjourney. This
                        technology is also being integrated into other tools.
                    </p>
                </Typography>
            </Grid>

            <Grid item xs={12} sm={12}>
                <Typography>
                    {fieldLabels.aiRisksInformation}
                </Typography>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="aiRisksInformation"
                    as={
                        <TextField
                            label={fieldLabels.pleaseEnterYourResponse}
                            readOnly={disabled}
                            data-test="aiRisksInformation-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.aiRisksInformation?.type === "maxLength" && (
                    <Error data-test="aiRisksInformation-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.aiRisksInformation?.type === "required" && (
                    <Error data-test="aiRisksInformation-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12} sm={12}>
                <Typography>
                    {fieldLabels.aiSystems}
                </Typography>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="aiSystems"
                    as={
                        <TextField
                            label={fieldLabels.pleaseEnterYourResponse}
                            readOnly={disabled}
                            data-test="aiSystems-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.aiSystems?.type === "maxLength" && (
                    <Error data-test="aiSystems-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.aiSystems?.type === "required" && (
                    <Error data-test="aiSystems-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>

            <Grid item xs={12} sm={12}>
                <Controller
                    control={control}
                    rules={{maxLength: 10000, required: true}}
                    name="aiCentres"
                    as={
                        <TextField
                            label={fieldLabels.aiCentres}
                            readOnly={disabled}
                            data-test="aiCentres-field"
                            rows={8}
                            multiline
                            required
                        />
                    }
                />
                {errors.aiCentres?.type === "maxLength" && (
                    <Error data-test="aiCentres-validation-length">
                        Must be no more than 10,000 characters
                    </Error>
                )}
                {errors.aiCentres?.type === "required" && (
                    <Error data-test="aiCentres-validation-required">
                        This field is required
                    </Error>
                )}
            </Grid>
        </Grid>
    );
};
